/**
 * SEO / AEO утилиты: мета-теги, canonical, Open Graph, JSON-LD.
 * Базовый URL: динамический (window.location.origin)
 */

export function getSiteUrl() {
  // Используем текущий домен для работы на prod, test, localhost
  const origin =
    (typeof window !== 'undefined' && window.location?.origin) ||
    import.meta.env.VITE_SITE_URL ||
    'https://chapterfour.ru'
  return origin.replace(/\/$/, '')
}

export function getCanonical(path = '') {
  const base = getSiteUrl()
  const p = path.startsWith('/') ? path : `/${path}`
  return p === '/' ? base : `${base}${p}`
}

function setTag(tag, attrs, content) {
  let el
  if (tag === 'title') {
    el = document.getElementsByTagName('title')[0]
    if (el) el.textContent = content || ''
    return
  }
  if (tag === 'link' && attrs.rel === 'canonical') {
    el = document.querySelector('link[rel="canonical"]')
    if (el) el.setAttribute('href', content)
    else {
      const l = document.createElement('link')
      l.rel = 'canonical'
      l.href = content
      l.setAttribute('data-dynamic-seo', '1')
      document.head.appendChild(l)
    }
    return
  }
  const nameOrProp = attrs.name ? 'name' : 'property'
  const key = attrs[nameOrProp]
  el = document.querySelector(`meta[${nameOrProp}="${key}"]`)
  if (el) el.setAttribute('content', content || '')
  else {
    const m = document.createElement('meta')
    m.setAttribute(nameOrProp, key)
    m.setAttribute('content', content || '')
    m.setAttribute('data-dynamic-seo', '1')
    document.head.appendChild(m)
  }
}

/**
 * Обновляет мета-теги и canonical из объекта seo.
 * @param {Object} seo - { title, description, keywords, robots, canonicalPath, ogImage, ogType, noindex }
 */
export function updateMeta(seo = {}) {
  const base = getSiteUrl()
  const canon =
    seo.canonicalPath != null
      ? getCanonical(seo.canonicalPath)
      : getCanonical(window.location.pathname)
  const def = {
    title: 'Chapterfour — медиа-продакшен полного цикла',
    description:
      'Коммерческое видео, реклама, дизайн, мобильная и web-разработка, графика, прямые трансляции, а также 3D motion.',
    ogImage: `${base}/metadata.png`,
    ogType: 'website',
    ogLocale: 'ru_RU',
    twitterCard: 'summary_large_image',
  }

  const title = seo.title
    ? seo.title.includes('Chapterfour')
      ? seo.title
      : `${seo.title} | Chapterfour`
    : def.title
  const desc = seo.description || def.description
  const robots = seo.noindex ? 'noindex, follow' : 'index, follow'

  document.getElementsByTagName('title')[0].textContent = title

  setTag('meta', { name: 'description' }, desc)
  if (seo.keywords) setTag('meta', { name: 'keywords' }, seo.keywords)
  setTag('meta', { name: 'robots' }, robots)

  const linkCanon = document.querySelector('link[rel="canonical"]')
  if (linkCanon) linkCanon.href = canon

  const img = seo.ogImage || def.ogImage
  setTag('meta', { property: 'og:title' }, title)
  setTag('meta', { property: 'og:description' }, desc)
  setTag('meta', { property: 'og:url' }, canon)
  setTag('meta', { property: 'og:image' }, img)
  setTag('meta', { property: 'og:type' }, seo.ogType || def.ogType)
  setTag('meta', { property: 'og:locale' }, seo.ogLocale || def.ogLocale)

  // og:image:secure_url, width, height, alt, type — только для дефолтного metadata.png; при своём изображении убираем
  const extra = [
    'og:image:secure_url',
    'og:image:width',
    'og:image:height',
    'og:image:alt',
    'og:image:type',
  ]
  if (!seo.ogImage && (img.includes('metadata.png') || img.endsWith('/metadata.png'))) {
    setTag('meta', { property: 'og:image:secure_url' }, img)
    setTag('meta', { property: 'og:image:width' }, '1200')
    setTag('meta', { property: 'og:image:height' }, '630')
    setTag('meta', { property: 'og:image:alt' }, 'Chapterfour — медиа-продакшен полного цикла')
    setTag('meta', { property: 'og:image:type' }, 'image/png')
  } else {
    extra.forEach((p) => {
      const el = document.querySelector(`meta[property="${p}"]`)
      if (el) el.remove()
    })
  }

  setTag('meta', { name: 'twitter:card' }, seo.twitterCard || def.twitterCard)
  setTag('meta', { name: 'twitter:title' }, title)
  setTag('meta', { name: 'twitter:description' }, desc)
  setTag('meta', { name: 'twitter:image' }, img)
}

/**
 * Удаляет/вставляет JSON-LD в head. Заменяет существующий script#seo-json-ld.
 */
export function setJsonLd(data) {
  const prev = document.getElementById('seo-json-ld')
  if (prev) prev.remove()
  if (!data) return
  const script = document.createElement('script')
  script.id = 'seo-json-ld'
  script.type = 'application/ld+json'
  script.textContent = typeof data === 'string' ? data : JSON.stringify(data)
  document.head.appendChild(script)
}

/**
 * Собирает глобальную разметку Organization + WebSite.
 */
export function getGlobalSchema() {
  const url = getSiteUrl()
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${url}/#organization`,
        name: 'Chapterfour',
        url,
        logo: { '@type': 'ImageObject', url: `${url}/favicon.ico` },
        description:
          'Медиа-продакшен полного цикла: коммерческое видео, реклама, дизайн, мобильная и web-разработка, графика, 3D motion.',
        sameAs: ['https://t.me/chapterf444'],
        address: { '@type': 'PostalAddress', addressCountry: 'RU' },
      },
      {
        '@type': 'WebSite',
        '@id': `${url}/#website`,
        url,
        name: 'Chapterfour — медиа-продакшен полного цикла',
        publisher: { '@id': `${url}/#organization` },
        inLanguage: 'ru-RU',
        description:
          'Коммерческое видео, реклама, дизайн, мобильная и web-разработка, графика, 3D motion.',
      },
    ],
  }
}

/**
 * VideoObject для VEO.
 */
export function getVideoSchema({
  name,
  description,
  thumbnailUrl,
  uploadDate,
  contentUrl,
  embedUrl,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: name || 'Видео',
    description: description || 'Видео от Chapterfour',
    thumbnailUrl: thumbnailUrl || undefined,
    uploadDate: uploadDate || undefined,
    contentUrl: contentUrl || undefined,
    embedUrl: embedUrl || undefined,
  }
}

/**
 * FAQPage для AEO.
 */
export function getFaqSchema(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  }
}

/**
 * Добавить дополнительный JSON-LD (например VideoObject) без удаления основного.
 * Удаляет только предыдущий блок с тем же id.
 */
export function addJsonLd(data, id = 'seo-json-ld-extra') {
  const prev = document.getElementById(id)
  if (prev) prev.remove()
  if (!data) return
  const script = document.createElement('script')
  script.id = id
  script.type = 'application/ld+json'
  script.textContent = typeof data === 'string' ? data : JSON.stringify(data)
  document.head.appendChild(script)
}
